import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';

import {
  formatPrice,
  formatPhoneNumber,
} from 'common/formatters';

import {
  hasRoleAdmin,
} from 'common/userRoles';

import {
  getSecurityDepositSettings,
  getMarketSecurityDepositLabel,
} from 'common/listingHelpers';

import {
  getHouseholdPets,
  calculateHouseholdDepositFees,
} from 'common/householdHelpers';

import {
  getLeaseTemplates,
} from 'common/leaseDrafting/leaseTemplates';

import {
  isApplicationAccepted,
} from 'common/householdApplication/applicationHelpers';

import {
  transformConcessionToTextList,
} from 'common/concessionHelpers';

import {
  LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL,
  LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_SUNROOM,
  LISTING_AGREEMENT_MANUAL,
  LISTING_AGREEMENT_CUSTOM,
} from 'common/listingConstants';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrCard, {
  SR_CARD_BULLET_COLOR_YELLOW,
  SR_CARD_BULLET_COLOR_PURPLE,
  SR_CARD_BULLET_COLOR_BLUE,
} from 'client/components/SrCard/SrCard';
import SrExternalLink from 'client/components/SrExternalLink';
import SrInfoTile, {
  SrInfoTileTextValue,
} from 'client/components/SrInfoTile/SrInfoTile';
import SrPartnerApprovalContext from 'client/components/SrPartnerApproval/SrPartnerApprovalContext';
import SrPartnerApprovalAddendaCheckbox from 'client/components/SrPartnerApproval/SrPartnerApprovalAddendaCheckbox';
import { CONCESSION_DESCRIPTION_VARIATION_PARTNER_APPROVAL } from 'common/concessionConstants';

const SrEditLink = ({ onClick }) => (
  <SrText
    color='blue300'
    font='greycliff'
    weight='600'
    size='14'
    lineHeight='20px'
    cursor='pointer'
    onClick={onClick}
  >
    Edit
  </SrText>
);

const SrPartnerApprovalFormReview = ({
  formikBag,
  onEditStep,
}) => {
  const {
    listingAgreementType,
    listingAgreementUrl,
    propertyOwnerName,
    propertyOwnerEmail,
    propertyOwnerPhone,
    managementName,
    managementEmail,
    managementPhone,
    repairContactName,
    repairContactEmail,
    repairContactPhone,
    partnerLeaseNotes,
  } = formikBag.values;
  const {
    history,
    partner,
    currentUser,
    listing,
    concession,
    market,
    householdListing,
    householdApplications,
    householdGuarantors,
    listingStripeAccount,
    partnerStripeAccount,
    openShowContactSupportModal,
  } = useContext(SrPartnerApprovalContext);

  const { nonEsaPets } = getHouseholdPets({
    householdApplications,
  });

  const feesForCharge = calculateHouseholdDepositFees({
    listing,
    market,
    householdListing,
    pets: nonEsaPets,
  });

  const {
    whoCollectsSecurityDepositPetFees,
    securityDepositCollectionInstructions,
  } = getSecurityDepositSettings({
    listing,
    partner,
  });

  const {
    leasingInformationEnabled,
  } = partner.experienceConfiguration;

  const securityDepositLabel = getMarketSecurityDepositLabel(market, true);

  const leaseTemplates = getLeaseTemplates({
    applications: householdApplications,
    guarantors: householdGuarantors,
    householdListing: householdListing,
    listing,
  }).filter(({ enabled }) => enabled);

  const stripeAccount = (listingStripeAccount || partnerStripeAccount);

  const goToRenterPetCriteria = () => {
    history.push(`/partner/${partner.slug}/listing/${listing.id}/edit/renter-pet-criteria?householdListingIdToApprove=${householdListing.id}&scrollTo=petDeposit`); // eslint-disable-line max-len
  };

  const goToFeeSettingsPage = () => {
    history.push(`/partner/${partner.slug}/listing/${listing.id}/edit/fee-settings?householdListingIdToApprove=${householdListing.id}`); // eslint-disable-line max-len
  };

  const landlordInfo = lodashCompact([
    propertyOwnerName,
    propertyOwnerEmail,
    formatPhoneNumber(propertyOwnerPhone),
  ]).join('\n');

  const managementInfo = lodashCompact([
    managementName,
    managementEmail,
    formatPhoneNumber(managementPhone),
  ]).join('\n');

  const repairContactInfo = lodashCompact([
    repairContactName,
    repairContactEmail,
    formatPhoneNumber(repairContactPhone),
  ]).join('\n');

  const householdIsApproved = householdApplications.every(isApplicationAccepted);

  const isAdmin = hasRoleAdmin(currentUser.roles);

  const shouldDisableEdit = householdIsApproved && !isAdmin;

  const leaseDetailsDOM = leasingInformationEnabled && (
    <SrCard
      color={SR_CARD_BULLET_COLOR_PURPLE}
      header='Lease Details'
      className='srCardCollectionMethod'
    >
      <SrBox marginTop='24px' marginBottom='6px'>
        {listingAgreementType === LISTING_AGREEMENT_MANUAL && (
          <>
            <SrInfoTile
              label='Landlord'
              value={landlordInfo}
              onEditClick={() => shouldDisableEdit
                ? openShowContactSupportModal()
                : onEditStep('property-owner')}
            />
            <SrBox height='39px' />
            {managementInfo && (
              <>
                <SrInfoTile
                  label='Managed by'
                  value={managementInfo}
                  onEditClick={() => shouldDisableEdit
                    ? openShowContactSupportModal()
                    : onEditStep('property-owner')}
                />
                <SrBox height='39px' />
              </>
            )}
            {repairContactInfo && partner.experienceConfiguration.repairContactEnabled && (
              <>
                <SrInfoTile
                  label='Repair contact '
                  value={repairContactInfo}
                  onEditClick={() => shouldDisableEdit
                    ? openShowContactSupportModal()
                    : onEditStep('property-owner')}
                />
                <SrBox height='39px' />
              </>
            )}
          </>
        )}
        {listingAgreementType === LISTING_AGREEMENT_CUSTOM && (
          <>
            <SrInfoTile
              label='Listing Agreement'
              value={(
                <SrExternalLink link={listingAgreementUrl}>
                  <SrInfoTileTextValue color='blue300' cursor='pointer'>
                    1 File Uploaded
                  </SrInfoTileTextValue>
                </SrExternalLink>
              )}
              onEditClick={() => shouldDisableEdit
                ? openShowContactSupportModal()
                : onEditStep('listing-agreement')}
            />
            <SrBox height='39px' />
          </>
        )}
        {partnerLeaseNotes && (
          <>
            <SrInfoTile
              label='Extra notes'
              value={partnerLeaseNotes}
              onEditClick={() => shouldDisableEdit
                ? openShowContactSupportModal()
                : onEditStep('extra-notes')}
            />
            <SrBox height='39px' />
          </>
        )}
        <SrInfoTile
          label='Addendums to include'
          value={(
            <>
              {leaseTemplates.map((templates) => {
                return (
                  <div key={templates.title}>
                    <SrPartnerApprovalAddendaCheckbox />
                    <SrText size='inherit' weight='inherit' font='inherit' paddingLeft='8px'>
                      {templates.title}
                    </SrText>
                    <br />
                  </div>
                );
              })}
            </>
          )}
          onEditClick={() => shouldDisableEdit
            ? openShowContactSupportModal()
            : onEditStep('addenda')}
        />
        <SrBox height='39px' />
      </SrBox>
    </SrCard>
  );

  return (
    <>
      <SrCard
        header='Payments to Collect'
        noContentPadding
      >
        <SrBox margin='22px 20px 6px 20px' paddingBottom='16px'>
          {feesForCharge.items.map((fee, index) => {
            const isLastElement = index === feesForCharge.items.length - 1;
            return (
              <SrBox key={fee.title} marginBottom={isLastElement ? null : '12px'}>
                <SrFlex justify='between'>
                  <SrFlex direction='col'>
                    <SrText
                      font='greycliff'
                      weight='600'
                      color='blueGreycliff'
                      size='16'
                      lineHeight='25px'
                    >
                      {fee.title}
                    </SrText>
                    {fee.subtitle && (
                      <SrText
                        color='blueGreycliff'
                        font='greycliff'
                        weight='600'
                        size='14'
                        lineHeight='20px'
                        opacity='0.5'
                      >
                        {fee.subtitle}
                      </SrText>
                    )}
                    {fee.quantity && (
                      <SrText
                        color='blueGreycliff'
                        font='greycliff'
                        weight='600'
                        size='14'
                        lineHeight='20px'
                        opacity='0.5'
                      >
                        Quantity: {fee.quantity}
                      </SrText>
                    )}
                    {fee.category === 'deposit' && (
                      <SrText
                        color='blueGreycliff'
                        font='greycliff'
                        weight='600'
                        size='14'
                        lineHeight='20px'
                        opacity='0.5'
                      >
                        {householdListing.securityDepositIncreaseMultiplier}x {securityDepositLabel} Amount
                      </SrText>
                    )}
                  </SrFlex>
                  <SrFlex direction='col' align='end'>
                    <SrText
                      font='greycliff'
                      weight='600'
                      color='blueGreycliff'
                      size='16'
                      lineHeight='30px'
                    >
                      {formatPrice(fee.value)}
                    </SrText>
                    {fee.category === 'deposit' && (
                      <SrEditLink
                        onClick={() => shouldDisableEdit
                          ? openShowContactSupportModal()
                          : onEditStep('security-deposit')
                        }
                      />
                    )}
                    {fee.category === 'pet' && (
                      <SrEditLink
                        onClick={shouldDisableEdit
                          ? openShowContactSupportModal
                          : goToRenterPetCriteria
                        }
                      />
                    )}
                    {fee.category === 'rent' && (
                      <SrEditLink
                        onClick={() => shouldDisableEdit
                          ? openShowContactSupportModal()
                          : onEditStep('collect-rent')}
                      />
                    )}
                  </SrFlex>
                </SrFlex>
              </SrBox>
            );
          })}
        </SrBox>
        <SrBox padding='22px 20px 4px 20px ' borderTop='1px solid rgba(123, 132, 136, 0.16)'>
          <SrFlex justify='between'>
            <SrText
              font='greycliff'
              weight='800'
              color='blueGreycliff'
              size='21'
              lineHeight='30px'
            >
              Total to collect
            </SrText>
            <SrText
              font='greycliff'
              weight='800'
              color='green'
              size='21'
              lineHeight='30px'
            >
              {formatPrice(feesForCharge.totalWithoutStripeFees)}
            </SrText>
          </SrFlex>
        </SrBox>
      </SrCard>
      {!!concession && (
        <SrCard
          header='Rent Concession'
          color={SR_CARD_BULLET_COLOR_BLUE}
          noContentPadding
        >
          <SrBox margin='22px 20px 6px 20px' paddingBottom='16px'>
            <SrBox marginTop='16px'>
              <SrInfoTile
                label='Concession Amount'
                value={formatPrice(concession.amount)}
              />
            </SrBox>
            <SrBox marginTop='16px'>
              <SrInfoTile
                label='Concession Details'
                value={transformConcessionToTextList({
                  concession,
                  rentPrice: listing.price,
                  variation: CONCESSION_DESCRIPTION_VARIATION_PARTNER_APPROVAL,
                }).join(' ')}
              />
            </SrBox>
          </SrBox>
        </SrCard>
      )}
      {(stripeAccount || securityDepositCollectionInstructions) && (
        <SrCard
          color={SR_CARD_BULLET_COLOR_YELLOW}
          header='Collection Method'
          className='srCardCollectionMethod'
        >
          <SrBox marginTop='24px' marginBottom='6px'>
            <SrInfoTile
              label='Service'
              value={whoCollectsSecurityDepositPetFees === LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL
                ? 'Alternative Method'
                : 'Sunroom EasyPay™️'
              }
              onEditClick={shouldDisableEdit
                ? openShowContactSupportModal
                : goToFeeSettingsPage
              }
            />
            <SrBox height='19px' />
            {whoCollectsSecurityDepositPetFees === LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_SUNROOM
              && stripeAccount && (
              <SrInfoTile
                label='Will be transferred to'
                value={(
                  <>
                    <SrText size='inherit' weight='inherit' font='inherit'>
                      {stripeAccount.displayName}
                    </SrText>
                    <br />
                    {stripeAccount.type === 'CreditDebitCard' && stripeAccount.cardLast4 && (
                      <>
                        <SrText size='inherit' weight='inherit' font='inherit'>
                          Debit: **{stripeAccount.cardLast4}
                        </SrText>
                        <br />
                      </>
                    )}
                    {stripeAccount.type === 'BankAccount' && stripeAccount.bankRoutingLast4 && (
                      <>
                        <SrText size='inherit' weight='inherit' font='inherit'>
                          Routing: **{stripeAccount.bankRoutingLast4}
                        </SrText>
                        <br />
                      </>
                    )}
                    {stripeAccount.type === 'BankAccount' && stripeAccount.bankAccountLast4 && (
                      <>
                        <SrText size='inherit' weight='inherit' font='inherit'>
                          Account: **{stripeAccount.bankAccountLast4}
                        </SrText>
                        <br />
                      </>
                    )}
                  </>
                )}
                onEditClick={shouldDisableEdit
                  ? openShowContactSupportModal
                  : goToFeeSettingsPage
                }
              />
            )}
            {whoCollectsSecurityDepositPetFees === LISTING_WHO_COLLECTS_LISTING_FEES_VALUE_MANUAL && (
              <SrInfoTile
                label='Payment Instructions'
                value={securityDepositCollectionInstructions}
                onEditClick={shouldDisableEdit
                  ? openShowContactSupportModal
                  : goToFeeSettingsPage
                }
              />
            )}
          </SrBox>
        </SrCard>
      )}
      {leaseDetailsDOM}
      {(listing.collectPetRent || listing.collectPetDeposit || listing.collectPetFee)
        && (!householdIsApproved || isAdmin) && (
        <SrFlex justify='center' marginTop='25px'>
          <SrText color='blue300' weight='600' cursor='pointer' onClick={goToRenterPetCriteria}>
            Edit Pet Deposit & Fees
          </SrText>
        </SrFlex>
      )}
    </>
  );
};

SrPartnerApprovalFormReview.propTypes = {
  formikBag: PropTypes.object.isRequired,
  onEditStep: PropTypes.func.isRequired,
};

SrPartnerApprovalFormReview.displayName = 'SrPartnerApprovalFormReview';

export default SrPartnerApprovalFormReview;
